import { SSE } from 'sse.js';
import auth from '@/plugins/auth';
import { monitorOptions } from '@/plugins/environments';

// SSE instance lives outside Vuex state to avoid reactivity overhead
let sseInstance = null;
let reconnectTimer = null;
let reconnectAttempt = 0;
const MAX_RECONNECT_DELAY = 30000;

function getSseUrl() {
  return `${monitorOptions.http.hostname}/sse/monitor`;
}

const state = {
  connected: false,
  segments: [],
  users: [],
  queues: [],
  calls: [],
};

const mutations = {
  connected(state, value) {
    state.connected = value;
  },
  segments(state, value) {
    state.segments = value;
  },
  users(state, value) {
    state.users = value;
  },
  queues(state, value) {
    state.queues = value;
  },
  calls(state, value) {
    state.calls = value;
  },
};

const actions = {
  connect({ commit, dispatch }) {
    dispatch('disconnect');

    const accessToken = auth.getTokens('accessToken');
    if (!accessToken) return;

    sseInstance = new SSE(getSseUrl(), {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    sseInstance.addEventListener('open', () => {
      reconnectAttempt = 0;
      commit('connected', true);
    });

    sseInstance.addEventListener('updates', (event) => {
      const { users, queues, calls, segments } = JSON.parse(event.data);
      // segments must be committed last - it reads usersNodes/queuesNodes/callsNodes
      // synchronously when assigned. See docs/adr/001-setter-ordering.md
      commit('users', users || []);
      commit('queues', queues || []);
      commit('calls', calls || []);
      commit('segments', segments || []);
    });

    sseInstance.addEventListener('error', () => {
      commit('connected', false);
      dispatch('scheduleReconnect');
    });

    sseInstance.addEventListener('abort', () => {
      commit('connected', false);
    });

    sseInstance.stream();
  },

  disconnect({ commit }) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;

    if (sseInstance) {
      sseInstance.close();
      sseInstance = null;
    }

    commit('connected', false);
  },

  scheduleReconnect({ dispatch }) {
    clearTimeout(reconnectTimer);
    const delay = Math.min(1000 * 2 ** reconnectAttempt, MAX_RECONNECT_DELAY);
    reconnectAttempt += 1;
    reconnectTimer = setTimeout(() => dispatch('connect'), delay);
  },
};

const getters = {
  connected: state => state.connected,
  segments: state => state.segments,
  users: state => state.users,
  queues: state => state.queues,
  calls: state => state.calls,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
